/* RESPONSIBLE TEAM: team-phone */
/* eslint-disable @intercom/intercom/no-bare-strings */

import type { FinVoiceVoiceLanguage } from './fin-voice-language-options';

export type FinVoiceVoiceOptions = {
  value: FinVoiceVoice;
  text: string;
};

// Universal voices (support 4+ languages) with neutral accent
export type FinVoiceUniversalVoice =
  | 'Mark'
  | 'Sully'
  | 'Archer'
  | 'Paul'
  | 'Felicity'
  | 'Nichalia'
  | 'Hope'
  | 'Chris'
  | 'Rory'
  | 'Labhaoise'
  | 'Juniper';

// English voices
export type FinVoiceEnglishVoice =
  | 'Alexander'
  | 'Hannah'
  | 'Jerry'
  | 'Oxley'
  | 'Chelsea'
  | 'Cassidy'
  | 'Lydia'
  | 'Jack'
  | 'April';

// Spanish voices
export type FinVoiceSpanishVoice = 'Carolina' | 'Ruiz' | 'Dora' | 'Lizy' | 'Paloma';

// Danish voices
export type FinVoiceDanishVoice = 'Matias' | 'Søren';

// Dutch voices
export type FinVoiceDutchVoice = 'Ruth' | 'Serge' | 'Arjen';

// French voices
export type FinVoiceFrenchVoice = 'Nicolas' | 'Lucie' | 'Alexandre';

// German voices
export type FinVoiceGermanVoice = 'Felix' | 'Ramona' | 'Lea' | 'Susi';

// Polish voices
export type FinVoicePolishVoice = 'Pawel' | 'Konwersacyjny' | 'Beata';

// Italian voices
export type FinVoiceItalianVoice = 'Violetta' | 'Raffaele';

// Arabic voices
export type FinVoiceArabicVoice = 'Salma' | 'Haytham';

// Japanese voices
export type FinVoiceJapaneseVoice = 'Kaori';

// Korean voices
export type FinVoiceKoreanVoice = 'Han Aim' | 'Harry Kim';

// Norwegian voices
export type FinVoiceNorwegianVoice = 'Helge' | 'Johannes';

// Russian voices
export type FinVoiceRussianVoice = 'Olga' | 'Arcadays';

// Swedish voices
export type FinVoiceSwedishVoice = 'Ola' | 'Sanna';

// Chinese voices
export type FinVoiceChineseVoice = 'Amy' | 'Yu' | 'Tong Wong';

// Hindi voices
export type FinVoiceHindiVoice = 'Anjali' | 'Raju';

// Indonesian voices
export type FinVoiceIndonesianVoice = 'Putra' | 'Zephlyn';

// Turkish voices
export type FinVoiceTurkishVoice = 'Orbay' | 'Meta Ecem';

// Ukrainian voices
export type FinVoiceUkrainianVoice = 'Mariya' | 'Alex';

// Vietnamese voices
export type FinVoiceVietnameseVoice = 'Nguyễn Luci' | 'Huyen';

// Czech voices
export type FinVoiceCzechVoice = 'Adam' | 'Oliver';

// Bulgarian voices
export type FinVoiceBulgarianVoice = 'Milena';

// Greek voices
export type FinVoiceGreekVoice = 'Theos' | 'Christos';

// Romanian voices
export type FinVoiceRomanianVoice = 'Mike' | 'Anca';

// Malay voices
export type FinVoiceMalayVoice = 'Athira' | 'Aisyah';

// Hungarian voices
export type FinVoiceHungarianVoice = 'Nolen' | 'David';

// Finnish voices
export type FinVoiceFinnishVoice = 'Jaakko';

// Portuguese voices
export type FinVoicePortugueseVoice = 'Dinis' | 'Roberta' | 'Kaique';

// Union type of all voices
export type FinVoiceVoice =
  | FinVoiceUniversalVoice
  | FinVoiceEnglishVoice
  | FinVoiceSpanishVoice
  | FinVoiceDanishVoice
  | FinVoiceDutchVoice
  | FinVoiceFrenchVoice
  | FinVoiceGermanVoice
  | FinVoicePolishVoice
  | FinVoiceItalianVoice
  | FinVoiceArabicVoice
  | FinVoiceJapaneseVoice
  | FinVoiceKoreanVoice
  | FinVoiceNorwegianVoice
  | FinVoiceRussianVoice
  | FinVoiceSwedishVoice
  | FinVoiceChineseVoice
  | FinVoiceHindiVoice
  | FinVoiceIndonesianVoice
  | FinVoiceTurkishVoice
  | FinVoiceUkrainianVoice
  | FinVoiceVietnameseVoice
  | FinVoiceCzechVoice
  | FinVoiceBulgarianVoice
  | FinVoiceGreekVoice
  | FinVoiceRomanianVoice
  | FinVoiceMalayVoice
  | FinVoiceHungarianVoice
  | FinVoiceFinnishVoice
  | FinVoicePortugueseVoice;

export const FIN_VOICE_11LABS_VOICES: {
  value: FinVoiceVoice;
  text: string;
  description: string[];
  supportedLanguages: string[];
}[] = [
  // Universal voices
  {
    value: 'Mark',
    text: 'Mark',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'ar',
      'da',
      'de',
      'es',
      'fr',
      'it',
      'ja',
      'ko',
      'nl',
      'no',
      'pl',
      'ru',
      'sv',
      'ta',
      'hi',
      'cs',
      'ro',
      'hu',
      'tr',
      'uk',
      'pt',
    ],
  },
  {
    value: 'Sully',
    text: 'Sully',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'ar',
      'de',
      'es',
      'fr',
      'it',
      'ja',
      'nl',
      'ru',
      'sv',
      'hi',
      'pt',
      'el',
      'cs',
      'bg',
      'tr',
      'uk',
    ],
  },
  {
    value: 'Jerry',
    text: 'Jerry',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'it', 'ko', 'nl', 'zh', 'ms', 'fi', 'ro', 'pt', 'ta'],
  },
  {
    value: 'Oxley',
    text: 'Oxley',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en'],
  },
  {
    value: 'Nichalia',
    text: 'Nichalia',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'de',
      'es',
      'fr',
      'it',
      'ko',
      'ru',
      'sv',
      'cs',
      'pl',
      'hi',
      'tr',
      'hu',
      'ro',
      'zh',
      'fi',
      'pt',
      'el',
      'ta',
    ],
  },
  {
    value: 'Hope',
    text: 'Hope',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: [
      'en',
      'es',
      'fr',
      'it',
      'ru',
      'ta',
      'pt',
      'cs',
      'bg',
      'tr',
      'hi',
      'hu',
      'ro',
      'el',
    ],
  },
  {
    value: 'Chelsea',
    text: 'Chelsea',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'da', 'es', 'ko', 'ru', 'sv', 'hi', 'bg', 'tr', 'hu', 'pt'],
  },
  {
    value: 'Lydia',
    text: 'Lydia',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'it', 'ko', 'nl', 'zh', 'ms', 'fi', 'ro', 'pt', 'ta'],
  },
  {
    value: 'Jack',
    text: 'Jack',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'it', 'ko', 'nl', 'zh', 'ms', 'fi', 'ro', 'pt', 'ta'],
  },
  {
    value: 'April',
    text: 'April',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'it', 'ko', 'nl', 'zh', 'ms', 'fi', 'ro', 'pt', 'ta'],
  },
  {
    value: 'Cassidy',
    text: 'Cassidy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'es', 'it', 'hi', 'pt', 'ro', 'hr', 'id', 'fil', 'sk', 'ta'],
  },
  {
    value: 'Juniper',
    text: 'Juniper',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.american-voice',
    ],
    supportedLanguages: ['en', 'ar', 'da', 'de', 'fr', 'it', 'pl', 'pt', 'hi', 'cs', 'tr', 'ro'],
  },
  // British voices
  {
    value: 'Chris',
    text: 'Chris',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'es',
      'fr',
      'ja',
      'ko',
      'nl',
      'no',
      'pl',
      'ru',
      'sv',
      'bg',
      'el',
      'tr',
      'hu',
      'fi',
      'cs',
      'ta',
    ],
  },
  {
    value: 'Archer',
    text: 'Archer',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'fr',
      'it',
      'nl',
      'ru',
      'sv',
      'pt',
      'hi',
      'pl',
      'el',
      'tr',
      'hu',
      'ro',
      'ta',
    ],
  },
  {
    value: 'Alexander',
    text: 'Alexander',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'ar',
      'es',
      'fr',
      'no',
      'ru',
      'hi',
      'pt',
      'el',
      'bg',
      'tr',
      'hu',
      'ro',
      'fi',
      'cs',
      'ta',
      'sv',
    ],
  },
  {
    value: 'Felicity',
    text: 'Felicity',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.british-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'fr',
      'it',
      'no',
      'ru',
      'sv',
      'hi',
      'bg',
      'pt',
      'fi',
      'cs',
    ],
  },
  // Australian voices
  {
    value: 'Paul',
    text: 'Paul',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.australian-voice',
    ],
    supportedLanguages: [
      'en',
      'da',
      'es',
      'it',
      'nl',
      'pl',
      'ru',
      'sv',
      'pt',
      'hi',
      'tr',
      'hu',
      'cs',
      'el',
    ],
  },
  {
    value: 'Hannah',
    text: 'Hannah',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.australian-voice',
    ],
    supportedLanguages: ['en'],
  },
  // Irish voices
  {
    value: 'Rory',
    text: 'Rory',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.irish-voice',
    ],
    supportedLanguages: ['en', 'da', 'it', 'ko', 'nl', 'pl', 'sv', 'fil', 'tr', 'pt'],
  },
  {
    value: 'Labhaoise',
    text: 'Labhaoise',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.irish-voice',
    ],
    supportedLanguages: ['en', 'da', 'fr', 'nl', 'sv', 'fil', 'hr', 'pt', 'ta'],
  },
  // Spanish voices
  {
    value: 'Carolina',
    text: 'Carolina',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.peninsular-spanish-voice',
    ],
    supportedLanguages: ['es'],
  },
  {
    value: 'Ruiz',
    text: 'Ruiz',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.peninsular-spanish-voice',
    ],
    supportedLanguages: ['es'],
  },
  {
    value: 'Dora',
    text: 'Dora Lanarra',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
  },
  {
    value: 'Lizy',
    text: 'Lizy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
  },
  {
    value: 'Paloma',
    text: 'Paloma',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.latin-american-spanish-voice',
    ],
    supportedLanguages: ['es'],
  },
  // Italian voices
  {
    value: 'Violetta',
    text: 'Violetta',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.italian-voice',
    ],
    supportedLanguages: [
      'it',
      'da',
      'en',
      'es',
      'fr',
      'ko',
      'nl',
      'pl',
      'ru',
      'hu',
      'tr',
      'hi',
      'id',
      'pt',
      'ta',
    ],
  },
  {
    value: 'Raffaele',
    text: 'Raffaele',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.italian-voice',
    ],
    supportedLanguages: ['it'],
  },
  // Polish voices
  {
    value: 'Pawel',
    text: 'Pawel',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: [
      'pl',
      'ar',
      'fr',
      'nl',
      'ru',
      'sv',
      'pt',
      'ja',
      'el',
      'bg',
      'ro',
      'ms',
      'ta',
    ],
  },
  {
    value: 'Konwersacyjny',
    text: 'Konwersacyjny',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: [
      'pl',
      'da',
      'de',
      'en',
      'es',
      'fr',
      'ko',
      'ru',
      'sv',
      'pt',
      'cs',
      'hi',
      'tr',
      'ms',
      'el',
      'ro',
    ],
  },
  {
    value: 'Beata',
    text: 'Beata',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.polish-voice',
    ],
    supportedLanguages: ['pl', 'ar', 'fr', 'ja', 'no', 'sv', 'tr', 'hi', 'ms', 'pt', 'ta'],
  },
  // German voices
  {
    value: 'Felix',
    text: 'Felix',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de', 'da', 'fr', 'it', 'ko', 'nl', 'sv', 'tr', 'hi', 'ro', 'el'],
  },
  {
    value: 'Susi',
    text: 'Susi',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de', 'da', 'fr', 'it', 'ko', 'nl', 'sv', 'tr', 'hi', 'ro', 'el'],
  },
  {
    value: 'Ramona',
    text: 'Ramona',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: ['de'],
  },
  {
    value: 'Lea',
    text: 'Lea',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.german-voice',
    ],
    supportedLanguages: [
      'de',
      'cs',
      'el',
      'es',
      'pt',
      'ar',
      'fi',
      'id',
      'fil',
      'tr',
      'hi',
      'hu',
      'ro',
      'ko',
      'en',
      'uk',
      'sk',
      'ta',
      'sv',
      'hr',
      'it',
      'da',
    ],
  },
  // French voices
  {
    value: 'Nicolas',
    text: 'Nicolas',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.french-voice',
    ],
    supportedLanguages: [
      'fr',
      'ar',
      'es',
      'it',
      'ja',
      'ko',
      'no',
      'pl',
      'ru',
      'sv',
      'hi',
      'cs',
      'tr',
      'hu',
      'ro',
      'ms',
      'fi',
      'id',
      'pt',
      'bg',
      'ta',
    ],
  },
  {
    value: 'Lucie',
    text: 'Lucie',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.french-voice',
    ],
    supportedLanguages: ['fr'],
  },
  {
    value: 'Alexandre',
    text: 'Alexandre',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.quebec-french-voice',
    ],
    supportedLanguages: [
      'fr',
      'ar',
      'da',
      'en',
      'es',
      'nl',
      'pl',
      'ru',
      'sv',
      'hi',
      'id',
      'uk',
      'tr',
      'hu',
      'pt',
      'el',
      'ta',
    ],
  },
  // Dutch voices
  {
    value: 'Ruth',
    text: 'Ruth',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: [
      'nl',
      'da',
      'en',
      'es',
      'it',
      'ru',
      'sv',
      'pt',
      'hi',
      'tr',
      'hu',
      'ro',
      'fi',
      'cs',
      'el',
    ],
  },
  {
    value: 'Serge',
    text: 'Serge',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: ['nl', 'ar', 'de', 'es', 'hi', 'el', 'pt', 'ro'],
  },
  {
    value: 'Arjen',
    text: 'Arjen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.dutch-voice',
    ],
    supportedLanguages: ['nl'],
  },
  // Danish voices
  {
    value: 'Søren',
    text: 'Søren',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.danish-voice',
    ],
    supportedLanguages: ['da', 'en', 'es', 'fr', 'it', 'ko', 'ru', 'sv', 'pt', 'tr', 'ta'],
  },
  {
    value: 'Matias',
    text: 'Matias',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.jutlandic-danish-voice',
    ],
    supportedLanguages: ['da', 'ko', 'ru', 'sv', 'pt', 'cs', 'tr', 'hu', 'fi', 'uk', 'ta', 'hr'],
  },
  // Arabic voices
  {
    value: 'Salma',
    text: 'Salma',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.levantine-arabic-voice',
    ],
    supportedLanguages: ['ar', 'da', 'en', 'es', 'fr', 'it', 'ta', 'hi', 'tr', 'cs', 'ms'],
  },
  {
    value: 'Haytham',
    text: 'Haytham',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.egyptian-arabic-voice',
    ],
    supportedLanguages: [
      'ar',
      'da',
      'en',
      'es',
      'fr',
      'nl',
      'no',
      'ru',
      'sv',
      'ta',
      'cs',
      'hi',
      'id',
      'ro',
      'uk',
      'zh',
      'ms',
    ],
  },
  // Korean voices
  {
    value: 'Han Aim',
    text: 'Han Aim',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.korean-voice',
    ],
    supportedLanguages: ['ko', 'da', 'en', 'es', 'ru', 'sv', 'tr', 'pt', 'sk', 'ro', 'hr'],
  },
  {
    value: 'Harry Kim',
    text: 'Harry Kim',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.korean-voice',
    ],
    supportedLanguages: ['ko', 'en', 'es', 'it', 'pl', 'ru', 'sv', 'ta', 'hi', 'pt', 'uk', 'ro'],
  },
  // Japanese voices
  {
    value: 'Kaori',
    text: 'Kaori',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.japanese-voice',
    ],
    supportedLanguages: ['ja', 'en', 'es', 'sk', 'ro'],
  },
  // Norwegian voices
  {
    value: 'Helge',
    text: 'Helge',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.oslo-norwegian-voice',
    ],
    supportedLanguages: ['no'],
  },
  {
    value: 'Johannes',
    text: 'Johannes',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.norwegian-voice',
    ],
    supportedLanguages: ['no'],
  },
  // Swedish voices
  {
    value: 'Ola',
    text: 'Ola',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.swedish-voice',
    ],
    supportedLanguages: ['sv'],
  },
  {
    value: 'Sanna',
    text: 'Sanna',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.stockholm-swedish-voice',
    ],
    supportedLanguages: [
      'sv',
      'de',
      'es',
      'fr',
      'it',
      'no',
      'ru',
      'ta',
      'cs',
      'hi',
      'bg',
      'ro',
      'el',
    ],
  },
  // Russian voices
  {
    value: 'Arcadays',
    text: 'Arcadays',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.russian-voice',
    ],
    supportedLanguages: ['ru', 'en', 'it', 'sv', 'tr', 'pt', 'id', 'hi', 'sk'],
  },
  {
    value: 'Olga',
    text: 'Olga',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.russian-voice',
    ],
    supportedLanguages: ['ru'],
  },
  // Chinese voices
  {
    value: 'Amy',
    text: 'Amy',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.beijing-mandarin-voice',
    ],
    supportedLanguages: ['zh', 'hi', 'fi', 'hr', 'nl', 'el', 'sk', 'pt', 'sv', 'fr', 'en', 'it'],
  },
  {
    value: 'Yu',
    text: 'Yu',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.taiwanese-mandarin-voice',
    ],
    supportedLanguages: ['zh'],
  },
  {
    value: 'Tong Wong',
    text: 'Tong Wong',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hong-kong-cantonese-voice',
    ],
    supportedLanguages: ['zh'],
  },
  // Hindi voices
  {
    value: 'Anjali',
    text: 'Anjali',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hindi-voice',
    ],
    supportedLanguages: ['hi', 'de', 'es', 'fr', 'ar', 'tr', 'ro', 'pt', 'sk', 'el', 'sv', 'da'],
  },
  {
    value: 'Raju',
    text: 'Raju',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hindi-voice',
    ],
    supportedLanguages: ['hi', 'pt', 'ru', 'el', 'cs', 'hr', 'fr', 'ar', 'nl', 'sv'],
  },
  // Indonesian voices
  {
    value: 'Putra',
    text: 'Putra',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.indonesian-voice',
    ],
    supportedLanguages: ['id'],
  },
  {
    value: 'Zephlyn',
    text: 'Zephlyn',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.balinese-indonesian-voice',
    ],
    supportedLanguages: ['id', 'ar', 'hi', 'ja', 'tr', 'ta', 'sv'],
  },
  // Turkish voices
  {
    value: 'Orbay',
    text: 'Orbay',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.turkish-voice',
    ],
    supportedLanguages: ['tr'],
  },
  {
    value: 'Meta Ecem',
    text: 'Meta Ecem',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.istanbul-turkish-voice',
    ],
    supportedLanguages: ['tr'],
  },
  // Ukrainian voices
  {
    value: 'Mariya',
    text: 'Mariya',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.ukrainian-voice',
    ],
    supportedLanguages: ['uk'],
  },
  {
    value: 'Alex',
    text: 'Alex',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.ukrainian-voice',
    ],
    supportedLanguages: ['uk', 'hu', 'ko', 'hr', 'sk', 'sv', 'ta', 'es'],
  },
  // Vietnamese voices
  {
    value: 'Nguyễn Luci',
    text: 'Nguyễn Luci',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.vietnamese-voice',
    ],
    supportedLanguages: ['vi'],
  },
  {
    value: 'Huyen',
    text: 'Huyen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.central-vietnamese-voice',
    ],
    supportedLanguages: [
      'vi',
      'es',
      'ar',
      'hu',
      'cs',
      'hr',
      'nl',
      'ro',
      'id',
      'en',
      'sv',
      'ta',
      'it',
    ],
  },
  // Czech voices
  {
    value: 'Adam',
    text: 'Adam',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.czech-voice',
    ],
    supportedLanguages: [
      'cs',
      'pl',
      'hi',
      'bg',
      'en',
      'fr',
      'ar',
      'hu',
      'ro',
      'pt',
      'sk',
      'sv',
      'it',
      'es',
    ],
  },
  {
    value: 'Oliver',
    text: 'Oliver',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.czech-voice',
    ],
    supportedLanguages: [
      'cs',
      'hi',
      'pl',
      'es',
      'bg',
      'ar',
      'tr',
      'ru',
      'nl',
      'ms',
      'fi',
      'en',
      'id',
      'sv',
      'fr',
    ],
  },
  // Bulgarian voices
  {
    value: 'Milena',
    text: 'Milena',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.bulgarian-voice',
    ],
    supportedLanguages: [
      'bg',
      'hu',
      'tr',
      'hi',
      'hr',
      'ru',
      'ro',
      'id',
      'el',
      'sk',
      'fr',
      'sv',
      'pl',
      'it',
      'da',
    ],
  },
  // Greek voices
  {
    value: 'Theos',
    text: 'Theos',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.greek-voice',
    ],
    supportedLanguages: [
      'el',
      'tr',
      'hi',
      'ko',
      'hr',
      'ru',
      'ro',
      'nl',
      'pt',
      'fr',
      'en',
      'pl',
      'sk',
      'sv',
      'ta',
    ],
  },
  {
    value: 'Christos',
    text: 'Christos',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.greek-voice',
    ],
    supportedLanguages: ['el'],
  },
  // Romanian voices
  {
    value: 'Mike',
    text: 'Mike',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.romanian-voice',
    ],
    supportedLanguages: [
      'ro',
      'hi',
      'pl',
      'es',
      'fr',
      'ar',
      'tr',
      'ms',
      'hr',
      'ko',
      'en',
      'pt',
      'sk',
      'uk',
      'sv',
      'ta',
      'no',
    ],
  },
  {
    value: 'Anca',
    text: 'Anca',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.romanian-voice',
    ],
    supportedLanguages: ['ro'],
  },
  // Malay voices
  {
    value: 'Athira',
    text: 'Athira',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.malay-voice',
    ],
    supportedLanguages: ['ms', 'ru', 'hr', 'hi', 'pt', 'uk', 'ta', 'es'],
  },
  {
    value: 'Aisyah',
    text: 'Aisyah',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.malay-voice',
    ],
    supportedLanguages: ['ms', 'es', 'tr', 'sk', 'en'],
  },
  // Hungarian voices
  {
    value: 'Nolen',
    text: 'Nolen',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hungarian-voice',
    ],
    supportedLanguages: [
      'hu',
      'pt',
      'es',
      'pl',
      'ru',
      'fr',
      'tr',
      'id',
      'ms',
      'uk',
      'sk',
      'ro',
      'ta',
      'sv',
    ],
  },
  {
    value: 'David',
    text: 'David',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.multilingual-male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.hungarian-voice',
    ],
    supportedLanguages: [
      'hu',
      'en',
      'cs',
      'pl',
      'es',
      'pt',
      'de',
      'it',
      'hi',
      'ru',
      'tr',
      'zh',
      'fi',
      'ro',
    ],
  },
  // Finnish voices
  {
    value: 'Jaakko',
    text: 'Jaakko',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.finnish-voice',
    ],
    supportedLanguages: ['fi'],
  },
  // Portuguese voices
  {
    value: 'Dinis',
    text: 'Dinis',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.portuguese-voice',
    ],
    supportedLanguages: ['pt'],
  },
  {
    value: 'Roberta',
    text: 'Roberta',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.female-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.brazilian-portuguese-voice',
    ],
    supportedLanguages: ['pt'],
  },
  {
    value: 'Kaique',
    text: 'Kaique',
    description: [
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.male-voice',
      'operator.fin.setup.tabs.setup-and-go-live.phone.voices.brazilian-portuguese-voice',
    ],
    supportedLanguages: ['pt'],
  },
];

export const DEFAULT_VOICE = 'Hope';

/**
 * Helper function to get ElevenLabs voices that support a specific language.
 * @param language - The language code (e.g., 'en-US', 'es-ES')
 * @returns Array of ElevenLabs voice objects that support the language
 */
export function getVoicesForLanguage(
  language: FinVoiceVoiceLanguage,
): typeof FIN_VOICE_11LABS_VOICES {
  // Extract the base language code (e.g., 'en' from 'en-US')
  let baseLanguage = language.split('-')[0];

  // Filter ElevenLabs voices using their supportedLanguages property
  return FIN_VOICE_11LABS_VOICES.filter((voice) =>
    voice.supportedLanguages.includes(baseLanguage),
  ).sort((a, b) => a.text.localeCompare(b.text));
}

/**
 * Helper function to check if an ElevenLabs voice supports a specific language.
 * @param voice - The voice name
 * @param language - The language code (e.g., 'en-US', 'es-ES')
 * @returns Boolean indicating if the voice supports the language
 */
export function doesVoiceSupportLanguage(
  voice: FinVoiceVoice,
  language: FinVoiceVoiceLanguage,
): boolean {
  let baseLanguage = language.split('-')[0];

  // Find the ElevenLabs voice
  let elevenLabsVoice = FIN_VOICE_11LABS_VOICES.find((v) => v.value === voice);

  if (elevenLabsVoice) {
    return elevenLabsVoice.supportedLanguages.includes(baseLanguage);
  }

  return false;
}
